import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, User, Save, Loader2, Mail, Building, MapPin, TrendingUp, Sparkles, 
  Settings as SettingsIcon, FileText, Image as ImageIcon, Globe, Upload, X, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CityAutocomplete } from '../components/CityAutocomplete';
import ProfilePhotoEditor from '../components/ProfilePhotoEditor';

const Settings = () => {
  const { user, getAuthHeaders } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const isAdmin = user?.is_admin;
  
  // Profile state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    company: '',
    city: '',
    state: '',
    country: ''
  });
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    email_enabled: true,
    frequency: 'immediate',
    client_upload: true,
    client_feedback: true,
    client_comment: true,
    client_alternative: true,
    designer_file: true,
    designer_comment: true,
    designer_material: true,
    // Trend alerts
    trend_alerts_enabled: true,
    trend_alerts_email: true,
    trend_alerts_frequency: 'daily',
    trend_alerts_threshold: 'all'
  });

  // CMS State (Admin only)
  const [siteConfig, setSiteConfig] = useState({
    company_name: '',
    logo_url: '',
    contact_email: '',
    privacy_url: '/privacy',
    terms_url: '/terms',
    hero: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    how_it_works: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    workflow: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    project_boards: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    client_approval: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    material_advisor: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    final_cta: { title: {}, subtitle: {}, description: {}, cta_text: {}, image_url: '' },
    footer_description: {},
    privacy_content: {},
    terms_content: {},
    social_links: {}
  });
  const [cmsActiveTab, setCmsActiveTab] = useState('branding');
  const [cmsSection, setCmsSection] = useState('hero');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API}/settings/profile`, { withCredentials: true }),
        axios.get(`${API}/settings/notifications`, { withCredentials: true })
      ];
      
      // Admin: also fetch site config
      if (user?.is_admin) {
        requests.push(axios.get(`${API}/site-config/admin`, { withCredentials: true }));
      }
      
      const [profileRes, notifRes, configRes] = await Promise.all(requests);
      
      setProfile({
        first_name: profileRes.data.first_name || '',
        last_name: profileRes.data.last_name || '',
        company: profileRes.data.company || '',
        city: profileRes.data.city || '',
        state: profileRes.data.state || ''
      });
      
      setNotifPrefs(notifRes.data);
      
      if (configRes) {
        setSiteConfig(configRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error(t('settings.loadError') || 'Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/profile`, profile, { withCredentials: true });
      toast.success('Profilo aggiornato');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, notifPrefs, { withCredentials: true });
      toast.success('Preferenze notifiche aggiornate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateNotifPref = (key, value) => {
    setNotifPrefs(prev => ({ ...prev, [key]: value }));
  };

  const saveSiteConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-config`, siteConfig, { withCredentials: true });
      toast.success('Configurazione sito aggiornata');
    } catch (error) {
      toast.error('Errore nel salvataggio della configurazione');
    } finally {
      setSaving(false);
    }
  };

  const updateSiteConfig = (field, value) => {
    setSiteConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateLocalizedField = (field, lang, value) => {
    setSiteConfig(prev => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 pt-28">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 shrink-0">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'notifications' 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
              >
                <Bell className="w-5 h-5" />
                <span>Notifiche Email</span>
              </button>
              <button
                onClick={() => setActiveTab('trends')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'trends' 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                <span>Trend Alerts</span>
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'profile' 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Profilo</span>
              </button>
              
              {/* CMS - Admin Only */}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab('cms')}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeTab === 'cms' 
                      ? 'bg-secondary text-foreground' 
                      : 'text-muted-foreground hover:bg-secondary/50'
                  }`}
                  data-testid="settings-cms-tab"
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span>CMS / Contenuti</span>
                </button>
              )}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1">
            {activeTab === 'notifications' && (
              <div className="bg-[#121212] border border-white/10 p-8">
                <div className="flex items-center gap-3 mb-8">
                  <Bell className="w-6 h-6" />
                  <div>
                    <h2 className="heading-display text-xl font-medium">Notifiche Email</h2>
                    <p className="text-sm text-muted-foreground">Gestisci quando ricevere notifiche via email</p>
                  </div>
                </div>

                {/* Master toggle */}
                <div className="flex items-center justify-between py-4 border-b border-border">
                  <div>
                    <p className="font-medium">Attiva notifiche email</p>
                    <p className="text-sm text-muted-foreground">Ricevi aggiornamenti via email</p>
                  </div>
                  <Switch
                    checked={notifPrefs.email_enabled}
                    onCheckedChange={(v) => updateNotifPref('email_enabled', v)}
                  />
                </div>

                {notifPrefs.email_enabled && (
                  <>
                    {/* Frequency */}
                    <div className="py-4 border-b border-border">
                      <Label className="block mb-3">Frequenza notifiche</Label>
                      <Select 
                        value={notifPrefs.frequency} 
                        onValueChange={(v) => updateNotifPref('frequency', v)}
                      >
                        <SelectTrigger className="w-full md:w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediata</SelectItem>
                          <SelectItem value="daily">Riepilogo giornaliero</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground mt-2">
                        {notifPrefs.frequency === 'immediate' 
                          ? 'Riceverai una email per ogni evento' 
                          : 'Riceverai un riepilogo giornaliero di tutte le attività'}
                      </p>
                    </div>

                    {/* Designer notifications */}
                    <div className="py-6">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                        Notifiche dai Clienti
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Foto ispirazionali</p>
                            <p className="text-sm text-muted-foreground">Quando un cliente carica una foto</p>
                          </div>
                          <Switch
                            checked={notifPrefs.client_upload}
                            onCheckedChange={(v) => updateNotifPref('client_upload', v)}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Feedback materiali</p>
                            <p className="text-sm text-muted-foreground">Quando un cliente approva o rifiuta</p>
                          </div>
                          <Switch
                            checked={notifPrefs.client_feedback}
                            onCheckedChange={(v) => updateNotifPref('client_feedback', v)}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Commenti</p>
                            <p className="text-sm text-muted-foreground">Quando un cliente lascia un commento</p>
                          </div>
                          <Switch
                            checked={notifPrefs.client_comment}
                            onCheckedChange={(v) => updateNotifPref('client_comment', v)}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Richieste alternative</p>
                            <p className="text-sm text-muted-foreground">Quando un cliente richiede un'alternativa</p>
                          </div>
                          <Switch
                            checked={notifPrefs.client_alternative}
                            onCheckedChange={(v) => updateNotifPref('client_alternative', v)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Client notifications */}
                    <div className="py-6 border-t border-border">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                        Notifiche dal Designer
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Queste notifiche vengono inviate ai clienti che hanno lasciato feedback sui tuoi progetti
                      </p>
                      
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Nuovi file</p>
                            <p className="text-sm text-muted-foreground">Quando carichi file al progetto</p>
                          </div>
                          <Switch
                            checked={notifPrefs.designer_file}
                            onCheckedChange={(v) => updateNotifPref('designer_file', v)}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Risposte ai commenti</p>
                            <p className="text-sm text-muted-foreground">Quando rispondi a una foto cliente</p>
                          </div>
                          <Switch
                            checked={notifPrefs.designer_comment}
                            onCheckedChange={(v) => updateNotifPref('designer_comment', v)}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Nuovi materiali</p>
                            <p className="text-sm text-muted-foreground">Quando aggiungi materiali al progetto</p>
                          </div>
                          <Switch
                            checked={notifPrefs.designer_material}
                            onCheckedChange={(v) => updateNotifPref('designer_material', v)}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="pt-6 border-t border-border">
                  <Button onClick={saveNotifications} disabled={saving} className="w-full md:w-auto">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salva Preferenze
                  </Button>
                </div>
              </div>
            )}

            {/* TREND ALERTS TAB */}
            {activeTab === 'trends' && (
              <div className="bg-[#121212] border border-white/10 p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="heading-display text-xl font-medium">Trend Alerts</h2>
                    <p className="text-sm text-muted-foreground">Ricevi notifiche quando i tuoi materiali diventano di tendenza</p>
                  </div>
                </div>

                {/* Master toggle */}
                <div className="flex items-center justify-between py-4 border-b border-border">
                  <div>
                    <p className="font-medium">Attiva Trend Alerts</p>
                    <p className="text-sm text-muted-foreground">Ricevi notifiche sui materiali in tendenza nei tuoi progetti</p>
                  </div>
                  <Switch
                    checked={notifPrefs.trend_alerts_enabled}
                    onCheckedChange={(v) => updateNotifPref('trend_alerts_enabled', v)}
                  />
                </div>

                {notifPrefs.trend_alerts_enabled && (
                  <>
                    {/* How it works */}
                    <div className="my-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-emerald-900">Come funziona</p>
                          <p className="text-sm text-emerald-700 mt-1">
                            Analizziamo periodicamente le ricerche, i salvataggi e le approvazioni dei clienti per identificare 
                            i materiali in tendenza. Riceverai una notifica quando un materiale nei tuoi board diventa popolare 
                            tra gli architetti.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Email toggle */}
                    <div className="flex items-center justify-between py-4 border-b border-border">
                      <div>
                        <p className="font-medium">Email digest giornaliero</p>
                        <p className="text-sm text-muted-foreground">Ricevi un riepilogo email dei trend ogni mattina</p>
                      </div>
                      <Switch
                        checked={notifPrefs.trend_alerts_email}
                        onCheckedChange={(v) => updateNotifPref('trend_alerts_email', v)}
                      />
                    </div>

                    {/* Threshold */}
                    <div className="py-4 border-b border-border">
                      <Label className="block mb-3">Sensibilità notifiche</Label>
                      <Select 
                        value={notifPrefs.trend_alerts_threshold} 
                        onValueChange={(v) => updateNotifPref('trend_alerts_threshold', v)}
                      >
                        <SelectTrigger className="w-full md:w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i trend</SelectItem>
                          <SelectItem value="rising">Solo trend in forte crescita</SelectItem>
                          <SelectItem value="top10">Solo Top 10 materiali</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground mt-2">
                        {notifPrefs.trend_alerts_threshold === 'all' && 'Riceverai notifiche per tutti i materiali che mostrano trend positivi'}
                        {notifPrefs.trend_alerts_threshold === 'rising' && 'Riceverai notifiche solo per materiali con crescita significativa'}
                        {notifPrefs.trend_alerts_threshold === 'top10' && 'Riceverai notifiche solo quando un materiale entra nella Top 10'}
                      </p>
                    </div>

                    {/* Stats preview */}
                    <div className="py-6">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                        I Tuoi Materiali Trending
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Visita la <a href="/trends" className="text-primary hover:underline">Dashboard Trend</a> per vedere 
                        tutti i materiali in tendenza e le statistiche dettagliate.
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-6 border-t border-border">
                  <Button onClick={saveNotifications} disabled={saving} className="w-full md:w-auto">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salva Preferenze
                  </Button>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="bg-[#121212] border border-white/10 p-8">
                <div className="flex items-center gap-3 mb-8">
                  <User className="w-6 h-6" />
                  <div>
                    <h2 className="heading-display text-xl font-medium">Profilo</h2>
                    <p className="text-sm text-muted-foreground">Gestisci le informazioni del tuo account</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Profile Photo Section */}
                  <ProfilePhotoEditor 
                    currentPhoto={profile.avatar_url}
                    onPhotoChange={(url) => setProfile(p => ({ ...p, avatar_url: url }))}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="first_name" className="mb-2 block">Nome</Label>
                      <Input
                        id="first_name"
                        value={profile.first_name}
                        onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name" className="mb-2 block">Cognome</Label>
                      <Input
                        id="last_name"
                        value={profile.last_name}
                        onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="mb-2 block">Email</Label>
                    <div className="flex items-center gap-2 px-4 py-3 bg-secondary/30 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span>{user?.email}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">L'email non può essere modificata</p>
                  </div>

                  <div>
                    <Label htmlFor="company" className="mb-2 block">Azienda / Studio</Label>
                    <div className="input-icon-wrapper">
                      <Building className="input-icon" />
                      <Input
                        id="company"
                        value={profile.company}
                        onChange={(e) => setProfile(p => ({ ...p, company: e.target.value }))}
                        className="input-field with-icon-left"
                        placeholder="Nome azienda o studio"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="city" className="mb-2 block">Città</Label>
                      <CityAutocomplete
                        value={profile.city}
                        onChange={(val) => setProfile(p => ({ ...p, city: val }))}
                        onCitySelect={(data) => {
                          setProfile(p => ({ 
                            ...p, 
                            city: data.city,
                            state: data.state || p.state,
                            country: data.country
                          }));
                        }}
                        placeholder="Cerca città..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="mb-2 block">Provincia/Stato</Label>
                      <Input
                        id="state"
                        value={profile.state}
                        onChange={(e) => setProfile(p => ({ ...p, state: e.target.value }))}
                        className="input-field"
                        placeholder="Provincia o stato"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-border">
                  <Button onClick={saveProfile} disabled={saving} className="w-full md:w-auto">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salva Profilo
                  </Button>
                </div>
              </div>
            )}

            {/* CMS TAB - Admin Only */}
            {activeTab === 'cms' && isAdmin && (
              <div className="bg-[#121212] border border-white/10 p-8">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                    <SettingsIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="heading-display text-xl font-medium">CMS - Gestione Contenuti</h2>
                    <p className="text-sm text-muted-foreground">Gestisci i contenuti del sito, logo, e pagine legali</p>
                  </div>
                </div>

                {/* CMS Sub-tabs */}
                <Tabs value={cmsActiveTab} onValueChange={setCmsActiveTab} className="w-full">
                  <TabsList className="mb-6 bg-secondary">
                    <TabsTrigger value="branding" className="data-[state=active]:bg-[#1a1a1a]">
                      <ImageIcon className="w-4 h-4 mr-2" /> Branding
                    </TabsTrigger>
                    <TabsTrigger value="homepage" className="data-[state=active]:bg-[#1a1a1a]">
                      <Globe className="w-4 h-4 mr-2" /> Homepage
                    </TabsTrigger>
                    <TabsTrigger value="legal" className="data-[state=active]:bg-[#1a1a1a]">
                      <FileText className="w-4 h-4 mr-2" /> Privacy & Termini
                    </TabsTrigger>
                  </TabsList>

                  {/* Branding Tab */}
                  <TabsContent value="branding" className="space-y-6">
                    <div>
                      <Label className="mb-2 block">Nome Azienda</Label>
                      <Input
                        value={siteConfig.company_name || ''}
                        onChange={(e) => updateSiteConfig('company_name', e.target.value)}
                        placeholder="Material Platform"
                        data-testid="cms-company-name"
                      />
                      <p className="text-sm text-muted-foreground mt-1">Appare nel footer e nel titolo del sito</p>
                    </div>

                    <div>
                      <Label className="mb-2 block">URL Logo</Label>
                      <Input
                        value={siteConfig.logo_url || ''}
                        onChange={(e) => updateSiteConfig('logo_url', e.target.value)}
                        placeholder="https://..."
                        data-testid="cms-logo-url"
                      />
                      <p className="text-sm text-muted-foreground mt-1">URL dell'immagine logo (consigliato SVG o PNG trasparente)</p>
                      {siteConfig.logo_url && (
                        <div className="mt-3 p-4 bg-stone-100 inline-block">
                          <img src={siteConfig.logo_url} alt="Logo preview" className="h-12 object-contain" />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="mb-2 block">Email Contatto</Label>
                      <Input
                        type="email"
                        value={siteConfig.contact_email || ''}
                        onChange={(e) => updateSiteConfig('contact_email', e.target.value)}
                        placeholder="support@example.com"
                        data-testid="cms-contact-email"
                      />
                    </div>
                  </TabsContent>

                  {/* Homepage Tab */}
                  <TabsContent value="homepage" className="space-y-8">
                    <div className="p-4 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg mb-6">
                      <p className="text-sm text-white/70">
                        I contenuti multilingua vengono visualizzati in base alla lingua selezionata dall'utente.
                        Compila i campi per tutte le 5 lingue supportate: EN, IT, ES, FR, DE.
                      </p>
                    </div>

                    {/* Section selector */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {[
                        { key: 'hero', label: 'Hero' },
                        { key: 'how_it_works', label: 'Come Funziona' },
                        { key: 'workflow', label: 'Workflow' },
                        { key: 'project_boards', label: 'Project Boards' },
                        { key: 'client_approval', label: 'Approvazione Cliente' },
                        { key: 'material_advisor', label: 'Material Advisor' },
                        { key: 'final_cta', label: 'CTA Finale' },
                      ].map(section => (
                        <button
                          key={section.key}
                          type="button"
                          onClick={() => setCmsSection(section.key)}
                          className={`px-4 py-2 text-sm border transition-colors ${
                            cmsSection === section.key 
                              ? 'bg-primary text-primary-foreground border-primary' 
                              : 'border-border hover:bg-secondary'
                          }`}
                        >
                          {section.label}
                        </button>
                      ))}
                    </div>

                    {/* Multilingual field helper component */}
                    {(() => {
                      const LANGS = [
                        { code: 'en', flag: '🇬🇧', name: 'English' },
                        { code: 'it', flag: '🇮🇹', name: 'Italiano' },
                        { code: 'es', flag: '🇪🇸', name: 'Español' },
                        { code: 'fr', flag: '🇫🇷', name: 'Français' },
                        { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
                      ];

                      const currentSection = siteConfig[cmsSection] || {};

                      const updateSectionField = (field, lang, value) => {
                        setSiteConfig(prev => ({
                          ...prev,
                          [cmsSection]: {
                            ...prev[cmsSection],
                            [field]: {
                              ...prev[cmsSection]?.[field],
                              [lang]: value
                            }
                          }
                        }));
                      };

                      const updateSectionImage = (value) => {
                        setSiteConfig(prev => ({
                          ...prev,
                          [cmsSection]: {
                            ...prev[cmsSection],
                            image_url: value
                          }
                        }));
                      };
                      
                      // Auto-translate function
                      const autoTranslate = async (field, sourceText) => {
                        if (!sourceText || sourceText.trim().length < 2) {
                          toast.error('Inserisci il testo italiano prima di tradurre');
                          return;
                        }
                        
                        try {
                          toast.loading('Traduzione in corso...', { id: 'translate' });
                          const headers = await getAuthHeaders();
                          const res = await axios.post(`${API}/site-config/translate`, {
                            text: sourceText,
                            source_lang: 'it',
                            target_langs: ['en', 'es', 'fr', 'de']
                          }, { headers });
                          
                          const translations = res.data.translations;
                          
                          // Update all translations
                          Object.entries(translations).forEach(([lang, text]) => {
                            if (lang !== 'it') {
                              updateSectionField(field, lang, text);
                            }
                          });
                          
                          toast.success('Traduzione completata!', { id: 'translate' });
                        } catch (err) {
                          console.error('Translation error:', err);
                          toast.error('Errore nella traduzione', { id: 'translate' });
                        }
                      };

                      return (
                        <div className="space-y-6 p-6 bg-stone-50 border border-stone-200">
                          <h3 className="font-medium text-lg capitalize">{cmsSection.replace(/_/g, ' ')}</h3>
                          
                          {/* Title */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="font-medium">Titolo</Label>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => autoTranslate('title', currentSection.title?.it)}
                                className="h-7 text-xs"
                              >
                                <Languages className="w-3 h-3 mr-1" /> Auto-traduci da IT
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {LANGS.map(lang => (
                                <div key={lang.code}>
                                  <span className="text-xs text-muted-foreground">{lang.flag} {lang.name}</span>
                                  <Input
                                    value={currentSection.title?.[lang.code] || ''}
                                    onChange={(e) => updateSectionField('title', lang.code, e.target.value)}
                                    placeholder={`Titolo in ${lang.name}`}
                                    className="mt-1"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Subtitle */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="font-medium">Sottotitolo</Label>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => autoTranslate('subtitle', currentSection.subtitle?.it)}
                                className="h-7 text-xs"
                              >
                                <Languages className="w-3 h-3 mr-1" /> Auto-traduci da IT
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {LANGS.map(lang => (
                                <div key={lang.code}>
                                  <span className="text-xs text-muted-foreground">{lang.flag} {lang.name}</span>
                                  <Input
                                    value={currentSection.subtitle?.[lang.code] || ''}
                                    onChange={(e) => updateSectionField('subtitle', lang.code, e.target.value)}
                                    placeholder={`Sottotitolo in ${lang.name}`}
                                    className="mt-1"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Description */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="font-medium">Descrizione</Label>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => autoTranslate('description', currentSection.description?.it)}
                                className="h-7 text-xs"
                              >
                                <Languages className="w-3 h-3 mr-1" /> Auto-traduci da IT
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {LANGS.map(lang => (
                                <div key={lang.code}>
                                  <span className="text-xs text-muted-foreground">{lang.flag} {lang.name}</span>
                                  <Textarea
                                    value={currentSection.description?.[lang.code] || ''}
                                    onChange={(e) => updateSectionField('description', lang.code, e.target.value)}
                                    placeholder={`Descrizione in ${lang.name}`}
                                    rows={2}
                                    className="mt-1"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* CTA Text */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="font-medium">Testo Pulsante (CTA)</Label>
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => autoTranslate('cta_text', currentSection.cta_text?.it)}
                                className="h-7 text-xs"
                              >
                                <Languages className="w-3 h-3 mr-1" /> Auto-traduci da IT
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {LANGS.map(lang => (
                                <div key={lang.code}>
                                  <span className="text-xs text-muted-foreground">{lang.flag} {lang.name}</span>
                                  <Input
                                    value={currentSection.cta_text?.[lang.code] || ''}
                                    onChange={(e) => updateSectionField('cta_text', lang.code, e.target.value)}
                                    placeholder={`Testo pulsante in ${lang.name}`}
                                    className="mt-1"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Section Image - With Upload */}
                          <div>
                            <Label className="mb-2 block font-medium">Immagine Sezione</Label>
                            <div className="flex flex-col gap-3">
                              {/* Upload Button */}
                              <div className="flex items-center gap-3">
                                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                                  <Upload className="w-4 h-4" />
                                  Carica Immagine
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      
                                      const formData = new FormData();
                                      formData.append('file', file);
                                      
                                      try {
                                        const authHeaders = await getAuthHeaders();
                                        const res = await axios.post(`${API}/uploads/image`, formData, {
                                          headers: { 
                                            ...authHeaders,
                                            'Content-Type': 'multipart/form-data'
                                          }
                                        });
                                        updateSectionImage(res.data.url);
                                        toast.success('Immagine caricata!');
                                      } catch (err) {
                                        toast.error('Errore caricamento immagine');
                                      }
                                    }}
                                  />
                                </label>
                                <span className="text-sm text-muted-foreground">oppure inserisci URL:</span>
                              </div>
                              <Input
                                value={currentSection.image_url || ''}
                                onChange={(e) => updateSectionImage(e.target.value)}
                                placeholder="https://..."
                              />
                              {currentSection.image_url && (
                                <div className="mt-2 p-2 bg-[#1a1a1a] inline-block border border-white/10 relative">
                                  <img src={currentSection.image_url} alt="Preview" className="h-24 object-contain" />
                                  <button
                                    onClick={() => updateSectionImage('')}
                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Footer Description - Always visible */}
                    <div className="p-6 bg-stone-50 border border-stone-200">
                      <h3 className="font-medium text-lg mb-4">Descrizione Footer</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          { code: 'en', flag: '🇬🇧', name: 'English' },
                          { code: 'it', flag: '🇮🇹', name: 'Italiano' },
                          { code: 'es', flag: '🇪🇸', name: 'Español' },
                          { code: 'fr', flag: '🇫🇷', name: 'Français' },
                          { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
                        ].map(lang => (
                          <div key={lang.code}>
                            <span className="text-xs text-muted-foreground">{lang.flag} {lang.name}</span>
                            <Textarea
                              value={siteConfig.footer_description?.[lang.code] || ''}
                              onChange={(e) => updateLocalizedField('footer_description', lang.code, e.target.value)}
                              placeholder={`Descrizione footer in ${lang.name}`}
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Legal Pages Tab */}
                  <TabsContent value="legal" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="mb-2 block">URL Pagina Privacy</Label>
                        <Input
                          value={siteConfig.privacy_url || ''}
                          onChange={(e) => updateSiteConfig('privacy_url', e.target.value)}
                          placeholder="/privacy"
                          data-testid="cms-privacy-url"
                        />
                        <p className="text-sm text-muted-foreground mt-1">Es: /privacy o link esterno</p>
                      </div>
                      <div>
                        <Label className="mb-2 block">URL Pagina Termini</Label>
                        <Input
                          value={siteConfig.terms_url || ''}
                          onChange={(e) => updateSiteConfig('terms_url', e.target.value)}
                          placeholder="/terms"
                          data-testid="cms-terms-url"
                        />
                        <p className="text-sm text-muted-foreground mt-1">Es: /terms o link esterno</p>
                      </div>
                    </div>

                    {/* Privacy Content */}
                    <div>
                      <Label className="mb-3 block font-medium">Contenuto Privacy Policy</Label>
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs text-muted-foreground">🇬🇧 English</span>
                          <Textarea
                            value={siteConfig.privacy_content?.en || ''}
                            onChange={(e) => updateLocalizedField('privacy_content', 'en', e.target.value)}
                            placeholder="Privacy policy content in English..."
                            rows={6}
                          />
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">🇮🇹 Italiano</span>
                          <Textarea
                            value={siteConfig.privacy_content?.it || ''}
                            onChange={(e) => updateLocalizedField('privacy_content', 'it', e.target.value)}
                            placeholder="Contenuto della privacy policy in italiano..."
                            rows={6}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Terms Content */}
                    <div>
                      <Label className="mb-3 block font-medium">Contenuto Termini di Servizio</Label>
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs text-muted-foreground">🇬🇧 English</span>
                          <Textarea
                            value={siteConfig.terms_content?.en || ''}
                            onChange={(e) => updateLocalizedField('terms_content', 'en', e.target.value)}
                            placeholder="Terms of service content in English..."
                            rows={6}
                          />
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">🇮🇹 Italiano</span>
                          <Textarea
                            value={siteConfig.terms_content?.it || ''}
                            onChange={(e) => updateLocalizedField('terms_content', 'it', e.target.value)}
                            placeholder="Contenuto dei termini di servizio in italiano..."
                            rows={6}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="pt-6 mt-6 border-t border-border">
                  <Button onClick={saveSiteConfig} disabled={saving} className="w-full md:w-auto">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Salva Configurazione
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Settings;
